import type { BcStoreUserRecord } from '../services/api';
import type { PermissionMatrixRow } from './permissions-audit-matrix';
import type { PermissionMetricRow } from './permissions-audit-metrics';
import {
  MATRIX_GRANT_FILTER_OPTIONS,
  uniqueSorted,
  USER_NO_PERMISSIONS,
} from './permissions-audit-filters';
import {
  formatStoreNameForDisplay,
} from './permissions-audit-store-label';

export interface FilterOption {
  value: string;
  label: string;
}

const MATRIX_COLUMN_VALUE_GETTERS: Record<
  'store_hash' | 'store_name' | 'email' | 'status',
  (row: PermissionMatrixRow) => string
> = {
  store_hash: (row) => row.storeHash,
  store_name: (row) => row.storeName,
  email: (row) => row.email,
  status: (row) => row.status,
};

export function buildMatrixColumnOptions(
  rows: PermissionMatrixRow[],
  columnKey: 'store_hash' | 'store_name' | 'email' | 'status',
): string[] | FilterOption[] {
  if (columnKey === 'store_name') {
    return buildStoreNameFilterOptions(rows.map((row) => row.storeName));
  }
  const getValue = MATRIX_COLUMN_VALUE_GETTERS[columnKey];
  return uniqueSorted(rows.map((row) => getValue(row)));
}

export function buildStoreNameFilterOptions(storeNames: string[]): FilterOption[] {
  return uniqueSorted(storeNames).map((storeName) => ({
    value: storeName,
    label: formatStoreNameForDisplay(storeName),
  }));
}

export function buildPermissionGrantOptions(): FilterOption[] {
  return [...MATRIX_GRANT_FILTER_OPTIONS];
}

export function buildUserColumnOptions(
  users: BcStoreUserRecord[],
  columnKey: 'email' | 'status' | 'store_hash' | 'store_name',
): string[] | FilterOption[] {
  if (columnKey === 'email') {
    return uniqueSorted(users.map((user) => user.email));
  }
  if (columnKey === 'store_hash') {
    return uniqueSorted(users.map((user) => user.storeHash));
  }
  if (columnKey === 'store_name') {
    return buildStoreNameFilterOptions(users.map((user) => user.storeName));
  }
  return uniqueSorted(users.map((user) => user.status));
}

export function buildUserNameOptions(users: BcStoreUserRecord[]): string[] {
  return uniqueSorted(
    users.map((user) => `${user.firstName} ${user.lastName}`.trim()),
  );
}

export function buildUserPermissionOptions(users: BcStoreUserRecord[]): string[] {
  const codes = users.flatMap((user) => user.permissions.map((permission) => permission.code));
  const options = uniqueSorted(codes);
  const hasEmpty = users.some((user) => user.permissions.length === 0);
  if (hasEmpty) {
    return [USER_NO_PERMISSIONS, ...options];
  }
  return options;
}

export function buildMetricColumnOptions(
  rows: PermissionMetricRow[],
  columnKey: 'permissionCode' | 'accessType' | 'userCount',
): string[] {
  if (columnKey === 'userCount') {
    return uniqueSorted(rows.map((row) => String(row.userCount)));
  }
  return uniqueSorted(rows.map((row) => row[columnKey]));
}

export function buildMetricWhoHasItOptions(rows: PermissionMetricRow[]): string[] {
  return uniqueSorted(rows.flatMap((row) => row.users));
}
